import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

import { fetchData } from '../../helpers/methods';
import { CustomerContext } from '../../contexts/customer-context';

import Button from '../../component-library/button';
import Breadcrumb from '../../component-library/breadcrumb';
import SpinnerContainer from '../../component-library/spinner-container';
import Pagination from '../../component-library/pagination';
import SearchCard from '../../component-library/search-card';
import SearchList from '../../component-library/search-list';

import Filter from './sub-components/filter';
import SearchBar from './sub-components/search-bar';

const Catalog = () => {
	const {
		search: {
			taxonomies: customerTaxonomyData,
		} = {},
		portalUIConfig: {
			mainNavigation: mainNavUiConfig = {},
		} = {},
	} = useContext(CustomerContext);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [loading, setLoading] = useState(true);
	const [activeSection, setActiveSection] = useState('catalog');
	const [showFilter, setShowFilter] = useState(false);
	const [data, setData] = useState([]);
	const [searchString, setSearchString] = useState('');
	const [viewMode, setViewMode] = useState(localStorage.getItem('StfUserViewMode') || 'card');
	const [query, setQuery] = useState({
		pageSize: localStorage.getItem('StfUserPageSize') || 24,
		pageOffset: 0,
		sortField: 'sortName',
		sortOrder: 'asc',
		searchString: '',
	});
	const [filter, setFilter] = useState({});
	const [pagination, setPagination] = useState({
		totalPages: 1,
		currentPage: 1,
	});
	const [breadcrumbData, setBreadcrumbData] = useState([
		{ name: 'Home', url: '/' },
		{ name: 'Catalog' },
	]);

	const getData = () => {
		setLoading(true);
		let filterString = '';

		if (filter && Object.keys(filter).length !== 0) {
			Object.entries(filter).forEach(entry => {
				const [key, value] = entry;
				if (value.length > 0) {
					filterString = filterString.concat(`&${key}=${value.toString()}`);
				}
			});
		}

		fetchData({
			uri: `/api/web/catalog/search?text=${query.searchString}&sortField=${query.sortField}:${query.sortOrder}&pageSize=${query.pageSize}&offset=${query.pageOffset}${filterString}`,
			method: 'GET',
		})
			.then(res => {
				const metaData = res.body.metadata;
				const listData = res.body.results;
				setPagination({
					...pagination,
					totalPages: Math.ceil(metaData.totalSize / query.pageSize),
				});
				setData(listData);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const handlePagination = currentPage => {
		const newOffset = (currentPage * query.pageSize) - query.pageSize;
		setPagination({
			...pagination,
			currentPage,
		});
		setQuery({
			...query,
			pageOffset: newOffset,
		});
	};

	const handleCardClick = id => {
		navigate(`details/${id}`);
	};

	useEffect(() => {
		localStorage.setItem('StfUserViewMode', viewMode);
	}, [viewMode]);

	useEffect(() => {
		const hasVerticalScrollbar = document.body.scrollHeight > document.body.clientHeight;
		if (showFilter === true && hasVerticalScrollbar) {
			document.body.style.overflow = 'hidden';
			document.body.style.paddingRight = '17px';
		} else {
			document.body.style.overflow = 'auto';
			document.body.style.paddingRight = '0';
		}
	}, [showFilter]);

	useEffect(() => {
		let newActiveSection = 'catalog';
		let newBreadcrumbData = [
			{ name: 'Home', url: '/' },
			{ name: 'Catalog' },
		];
		if (pathname.includes('top-apis')) {
			newActiveSection = 'top-apis';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Top APIs' }];
		}
		if (pathname.includes('favorites')) {
			newActiveSection = 'favorites';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Favourites' }];
		}
		if (pathname.includes('taxonomy')) {
			newActiveSection = 'taxonomy';
			newBreadcrumbData = [{ name: 'Home', url: '/' }, { name: 'Catalog', url: '/catalog' }, { name: 'Taxonomy' }];
		}
		setBreadcrumbData(newBreadcrumbData);
		setActiveSection(newActiveSection);
	}, [pathname]);

	useEffect(() => {
		getData();
	}, [query, filter]);

	return (
		<>
			<Breadcrumb
				data={breadcrumbData}
			/>
			<div className="search__grid">
				<div className="left-navigation">
					<button
						className={`${activeSection === 'catalog' && 'is-active'}`}
						onClick={() => navigate('/catalog')}
						type="button"
					>
						Catalog
					</button>
					{(mainNavUiConfig?.taxonomy?.enabled !== false && customerTaxonomyData?.length > 0) ? (
						<button
							className={`${activeSection === 'taxonomy' && 'is-active'}`}
							onClick={() => navigate('/catalog/taxonomy')}
							type="button"
						>
							Taxonomy
						</button>
					) : null}
					{mainNavUiConfig?.topApis?.enabled !== false && (
						<button
							className={`${activeSection === 'top-apis' && 'is-active'}`}
							onClick={() => navigate('/catalog/top-apis')}
							type="button"
						>
							Top APIs
						</button>
					)}
					<button
						className={`${activeSection === 'favorites' && 'is-active'}`}
						onClick={() => navigate('/catalog/favorites')}
						type="button"
					>
						My Favorites
					</button>
				</div>
				{activeSection === 'catalog' ? (
					<div>
						<SearchBar
							searchString={searchString}
							setSearchString={newSearchString => setSearchString(newSearchString)}
							query={query}
							setQuery={newQuery => setQuery(newQuery)}
							pagination={pagination}
							setPagination={newPagination => setPagination(newPagination)}
							filter={filter}
							setShowFilter={bool => setShowFilter(bool)}
							viewMode={viewMode}
							setViewMode={mode => setViewMode(mode)}
						/>
						{loading && (
							<SpinnerContainer />
						)}
						{!loading && (
							data?.length > 0 ? (
								<div>
									{viewMode === 'card' && (
										<div className="search__results-grid">
											{data.map(item => (
												<SearchCard
													key={item.id}
													id={item.id}
													title={item.name}
													interfaceType={item.interfaceType?.name}
													version={item.version}
													description={item.description}
													state={item.state}
													division={item.division}
													favourite={item.userFavourite}
													subscribed={item.userWatched}
													objectType={item.objectType}
													handleCardClick={id => handleCardClick(id)}
												/>
											))}
										</div>
									)}
									{viewMode === 'list' && (
										<div>
											{data.map(item => (
												<SearchList
													key={item.id}
													id={item.id}
													title={item.name}
													interfaceType={item.interfaceType?.name}
													version={item.version}
													description={item.description}
													state={item.state}
													objectType={item.objectType}
													handleCardClick={id => handleCardClick(id)}
												/>
											))}
										</div>
									)}
									<Pagination
										totalPages={pagination.totalPages}
										currentPage={pagination.currentPage}
										action={currentPage => handlePagination(currentPage)}
									/>
								</div>
							) : (
								<div>
									{Object.keys(filter).length ? (
										<>
											<p className="grey-text text-center margin-top-24">No results found</p>
											<p className="grey-text text-center margin-top-24">You have filters set which may limit your search results</p>
											<div className="text-center">
												<Button
													type="secondary"
													label="Clear Filters"
													onClick={() => setFilter({})}
												/>
											</div>
										</>
									) : (
										<p className="grey-text text-center margin-top-24">No results found</p>
									)}
								</div>
							)
						)}
					</div>
				) : (
					<Outlet />
				)}
			</div>
			{showFilter && (
				<Filter
					filter={filter}
					setFilter={newFilter => setFilter(newFilter)}
					showFilter={showFilter}
					setShowFilter={bool => setShowFilter(bool)}
				/>
			)}
		</>
	);
};

export default Catalog;
